import { generate } from '@ant-design/colors'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchUserAnalyticsGraphParams,
  UserAnalyticsGraphVisitsSpend,
} from '@models/user/UserAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedUser } from '@store/UserReducer'
import { currencyFormat } from '@utils/currencyFormatter'
import { Card, Empty, Flex, Segmented, Spin, theme } from 'antd'
import { useEffect, useState } from 'react'
import { ResponsiveContainer, Treemap } from 'recharts'

const VisitedVenues = () => {
  const { token } = theme.useToken()

  const colors = generate(token.colorPrimary)

  const user = useAppSelector(selectSelectedUser())
  const { selectedDates } = useAnalytics()

  const [chartData, setChartData] = useState<UserAnalyticsGraphVisitsSpend[]>(
    []
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [selectedKPI, setSelectedKPI] = useState<'count' | 'spend'>('count')

  const fetchData = async () => {
    if (!user) return

    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData([])
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchUserAnalyticsGraphParams = {
      entity: 'user',
      entityId: user.id,
      kind: 'visits-spend',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
    }

    try {
      const response =
        await fetchAnalyticsGraph<UserAnalyticsGraphVisitsSpend[]>(fetchParams)
      const data = response.data
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [user, selectedDates])

  interface CustomizedAxisTickProps {
    root: any
    depth: any
    x: any
    y: any
    width: number
    height: number
    index: number
    payload: any
    colors: string[]
    rank: number
    name: string
    count: number
    spend: number
  }
  const CustomizedContent: React.FC<CustomizedAxisTickProps> = ({
    root,
    depth,
    x,
    y,
    width,
    height,
    index,
    colors,
    name,
    count,
    spend,
  }: CustomizedAxisTickProps) => {
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill:
              depth <= 1
                ? colors[Math.floor((index / root.children.length) * 6)]
                : '#ffffff00',
            stroke: '#fff',
            strokeWidth: 1,
          }}
        />
        {depth === 1 && (
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            fill="#000"
            strokeOpacity={0}
            fontSize={13}
            fillOpacity={1}
          >
            <tspan x={x + width / 2} dy="-0.3em">
              {name}
            </tspan>
            {selectedKPI === 'count' && (
              <tspan x={x + width / 2} dy="1.2em">
                {count} times
              </tspan>
            )}
            {selectedKPI === 'spend' && (
              <tspan x={x + width / 2} dy="1.2em">
                {currencyFormat(spend, true)}
              </tspan>
            )}
          </text>
        )}
      </g>
    )
  }
  const CustomContentWithColors: React.FC<any> = (props) => (
    <CustomizedContent {...props} colors={colors} />
  )
  return (
    <Card
      style={{ alignContent: 'center', textAlign: 'center', minHeight: 450 }}
    >
      {isLoading && <Spin />}
      {(chartData.length === 0 || error) && !isLoading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
      )}
      {chartData.length > 0 && !isLoading && !error && (
        <Flex vertical gap={20}>
          <Segmented
            block
            defaultValue={selectedKPI}
            options={[
              {
                value: 'count',
                label: 'Count',
              },
              {
                value: 'spend',
                label: 'Spend',
              },
            ]}
            onChange={(e) => {
              setSelectedKPI(e as any)
            }}
          />
          <ResponsiveContainer width="100%" height={330}>
            <Treemap
              width={400}
              height={200}
              data={chartData as any}
              dataKey={selectedKPI}
              stroke="#fff"
              fill={token.colorPrimary}
              content={<CustomContentWithColors />}
            />
          </ResponsiveContainer>
        </Flex>
      )}
    </Card>
  )
}

export default VisitedVenues
